import type { TFunction } from "@bounce/i18n"

import type { NearbyStorageSpot } from "../../libs/bounce/stores/types"
import { ReviewReason } from "@/libs/bounce/bounceSdk.types.gen"

const MAX_REVIEW_REASONS = 3

const ALLOWED_REVIEW_REASONS = [
  ReviewReason.AdditionalSecurity,
  ReviewReason.ConvenientLocation,
  ReviewReason.EasyCico,
  ReviewReason.FastService,
  ReviewReason.FriendlyStaff,
  ReviewReason.SafeLocation,
  ReviewReason.GreatValue,
]

// selects a stable variation index for a reason based on storeId, for consistent SSR-safe output
function getStableIndex(seed: string, reason: string, length: number): number {
  let hash = 0
  const input = seed + reason
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i)
    hash |= 0
  }

  return Math.abs(hash) % length
}

const translateReviewReason = (reason: ReviewReason, t: TFunction, storeId: string): string | null => {
  const getRandomVariation = (variations: string[]) =>
    variations[getStableIndex(storeId, reason, variations.length)] ?? null

  switch (reason) {
    case ReviewReason.AdditionalSecurity:
      return getRandomVariation([
        t("hooks.useReviewReasonTranslation.additionalSecurity", "additional security"),
        t("hooks.useReviewReasonTranslation.enhancedSecurityMeasures", "enhanced security measures"),
        t("hooks.useReviewReasonTranslation.extraSecurityFeatures", "extra security features"),
        t("hooks.useReviewReasonTranslation.superiorProtection", "superior protection"),
        t("hooks.useReviewReasonTranslation.advancedSafety", "advanced safety"),
        t("hooks.useReviewReasonTranslation.robustSecurity", "robust security"),
      ])
    case ReviewReason.ConvenientLocation:
      return getRandomVariation([
        t("hooks.useReviewReasonTranslation.convenientLocation", "convenient location"),
        t("hooks.useReviewReasonTranslation.greatLocation", "great location"),
        t("hooks.useReviewReasonTranslation.perfectSpot", "perfect spot"),
        t("hooks.useReviewReasonTranslation.idealStorage", "ideal storage"),
        t("hooks.useReviewReasonTranslation.primeLocation", "prime location"),
      ])
    case ReviewReason.EasyCico:
      return getRandomVariation([
        t("hooks.useReviewReasonTranslation.easyDropOffAndPickUp", "easy drop-off and pick-up"),
        t("hooks.useReviewReasonTranslation.smoothCheckInProcess", "smooth check-in process"),
        t("hooks.useReviewReasonTranslation.hassleFreeService", "hassle-free service"),
        t("hooks.useReviewReasonTranslation.seamlessProcess", "seamless process"),
        t("hooks.useReviewReasonTranslation.effortlessExperience", "effortless experience"),
        t("hooks.useReviewReasonTranslation.quickAndEasyProcess", "quick and easy process"),
        t("hooks.useReviewReasonTranslation.streamlinedService", "streamlined service"),
      ])
    case ReviewReason.FastService:
      return getRandomVariation([
        t("hooks.useReviewReasonTranslation.fastService", "fast service"),
        t("hooks.useReviewReasonTranslation.quickAndEfficient", "quick and efficient"),
        t("hooks.useReviewReasonTranslation.speedyService", "speedy service"),
        t("hooks.useReviewReasonTranslation.rapidResponse", "rapid response"),
        t("hooks.useReviewReasonTranslation.swiftService", "swift service"),
        t("hooks.useReviewReasonTranslation.instantService", "instant service"),
      ])
    case ReviewReason.FriendlyStaff:
      return getRandomVariation([
        t("hooks.useReviewReasonTranslation.friendlyStaff", "friendly staff"),
        t("hooks.useReviewReasonTranslation.helpfulTeam", "helpful team"),
        t("hooks.useReviewReasonTranslation.welcomingService", "welcoming service"),
        t("hooks.useReviewReasonTranslation.professionalTeam", "professional team"),
        t("hooks.useReviewReasonTranslation.amazingCustomerService", "amazing customer service"),
        t("hooks.useReviewReasonTranslation.exceptionalService", "exceptional service"),
      ])
    case ReviewReason.OpenLate:
      return getRandomVariation([
        t("hooks.useReviewReasonTranslation.openLate", "open late"),
        t("hooks.useReviewReasonTranslation.extendedHours", "extended hours"),
        t("hooks.useReviewReasonTranslation.convenientHours", "convenient hours"),
        t("hooks.useReviewReasonTranslation.flexibleSchedule", "flexible schedule"),
        t("hooks.useReviewReasonTranslation.accommodatingHours", "accommodating hours"),
        t("hooks.useReviewReasonTranslation.longerOperatingHours", "longer operating hours"),
        t("hooks.useReviewReasonTranslation.availableWhenNeeded", "available when needed"),
      ])
    case ReviewReason.SafeLocation:
      return getRandomVariation([
        t("hooks.useReviewReasonTranslation.safeLocation", "safe location"),
        t("hooks.useReviewReasonTranslation.secureArea", "secure area"),
        t("hooks.useReviewReasonTranslation.trustworthyLocation", "trustworthy location"),
        t("hooks.useReviewReasonTranslation.safeAndSecure", "safe and secure"),
        t("hooks.useReviewReasonTranslation.reliableSpot", "reliable spot"),
      ])
    case ReviewReason.GreatValue:
      return getRandomVariation([
        t("hooks.useReviewReasonTranslation.greatValue", "great value"),
        t("hooks.useReviewReasonTranslation.excellentPrice", "excellent price"),
        t("hooks.useReviewReasonTranslation.affordableRates", "affordable rates"),
        t("hooks.useReviewReasonTranslation.budgetFriendly", "budget-friendly"),
        t("hooks.useReviewReasonTranslation.competitivePricing", "competitive pricing"),
        t("hooks.useReviewReasonTranslation.unbeatable value", "unbeatable value"),
      ])
    case ReviewReason.Other:
      return t("hooks.useReviewReasonTranslation.other", "other")
    default:
      return null
  }
}

type GetReviewSummaryTextProps = Pick<NearbyStorageSpot, "reviewSummary"> & {
  t: TFunction
  storeId: string
}

export const getReviewSummaryText = ({ reviewSummary, storeId, t }: GetReviewSummaryTextProps): string | null => {
  const reasons = reviewSummary.reasonCounts
    ?.filter(({ reason }) => ALLOWED_REVIEW_REASONS.includes(reason))
    .slice(0, MAX_REVIEW_REASONS)

  if (!reasons?.length) return null

  const [reasonOne, reasonTwo, reasonThree] = reasons.map(({ reason }) => translateReviewReason(reason, t, storeId))

  return t(
    "cmp.storeCard.reviewReasonsLabel",
    "Customers love the {reasonCount, select, 1 {{reasonOne}} 2 {{reasonOne}, and {reasonTwo}} other {{reasonOne}, {reasonTwo}, and {reasonThree}}}",
    {
      reasonCount: reasons.length,
      reasonOne,
      reasonTwo,
      reasonThree,
    },
  )
}
